<template>
  <div class="table-wrapper" id="searchPageTable" :class="{ freezeHeaderRow }">
    <table class="search-page-table" id="searchPageTable">
      <thead v-if="showHeaders">
        <tr>
          <th
            v-for="header in tableHeaders"
            :key="header.key"
            :class="isActiveSort(sortKey, header.key)"
            @click="applySortBy(header.key)"
          >
            <span class="header-label">
              {{ header.label }}
              <span
                v-if="header.sortable"
                :class="getSortArrowIcon(sortKey, sortOrder, header.key)"
              >
                {{ getSortArrow(sortKey, sortOrder, header.key) }}
              </span>
            </span>
          </th>
        </tr>
      </thead>
      <tbody v-if="showRows">
        <SearchPageSearchResultsTableRow
          v-for="result in results"
          :key="`${result.referenceId}_${result.id}`"
          :result="result"
          :headers="tableHeaders"
        />
      </tbody>
    </table>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import SearchPageSearchResultsTableRow from "@/components/v2/search-page/SearchPageSearchResultsTableRow.vue";
import { TableHeader } from "@/models/table";
import {
  isActiveSort,
  getSortArrow,
  sort,
  sortBy,
  getSortArrowIcon,
} from "@/composables/tableUtils";
import { CertificationSearchResultModel } from "@/types/certifications";
import { DisbursementSearchResultModel } from "@/types/disbursements";

const props = withDefaults(
  defineProps<{
    results: Array<
      CertificationSearchResultModel | DisbursementSearchResultModel
    >;
    freezeHeaderRow: boolean;
    showHeaders?: boolean;
    showRows?: boolean;
  }>(),
  {
    showHeaders: true,
    showRows: true,
    freezeHeaderRow: true,
    results: () => [], // Use a factory function for array/object defaults
  }
);

const route = useRoute();
const router = useRouter();

const sortKey = ref<string>("");
const sortOrder = ref<number>(1);

const headers = ref<Array<TableHeader>>([
  {
    label: "Borrower",
    sortable: true,
    key: "borrowerName",
    sortProperty: "borrowerName",
    display: true,
    title: "Name sort by last name.",
  },
  {
    label: "Status",
    sortable: true,
    key: "certificationStatusTypeDescription",
    sortProperty: "certificationStatusTypeDescription",
    display: true,
  },
  {
    label: "Lender",
    sortable: true,
    key: "lenderName",
    sortProperty: "lenderName",
    display: true,
  },
  {
    label: "App ID",
    sortable: true,
    key: "referenceId",
    sortProperty: "referenceId",
    display: true,
  },
  {
    label: "Loan Amt",
    title: "Loan Amount",
    sortable: true,
    key: "loanAmountFormatted",
    sortProperty: "loanAmount",
    display: true,
  },
]);

const disbursementsTableHeaders = ref<Array<TableHeader>>([
  {
    label: "Disb. Date",
    name: "DisbursementDate",
    title: "Disbursement Date",
    sortable: true,
    key: "disbursementDateDisplay",
    sortProperty: "disbursementDateDisplay",
    display: true,
  },
  {
    label: "Method",
    sortable: true,
    key: "disbursementMethodTypeDescription",
    sortProperty: "disbursementMethodTypeDescription",
    display: true,
  },
  {
    label: "Borrower",
    sortable: true,
    key: "borrowerName",
    sortProperty: "borrowerName",
    display: true,
    title: "Name sort by last name.",
  },
  {
    label: "Status",
    sortable: true,
    key: "disbursementStatusTypeName",
    sortProperty: "disbursementStatusTypeName",
    display: true,
  },
  {
    label: "Lender",
    sortable: true,
    key: "lenderName",
    sortProperty: "lenderName",
    display: true,
  },
  {
    label: "App ID",
    sortable: true,
    key: "referenceId",
    sortProperty: "referenceId",
    display: true,
  },
  {
    label: "Disb. Amt",
    title: "Disbursement Amount",
    sortable: true,
    key: "disbursementAmountFormatted",
    sortProperty: "disbursementAmount",
    display: true,
  },
]);

const tableHeaders = computed<Array<TableHeader>>(() => {
  if (route.name === "Certifications") {
    return headers.value.filter((t: TableHeader) => t.display);
  } else if (route.name === "Disbursements") {
    return disbursementsTableHeaders.value.filter(
      (t: TableHeader) => t.display
    );
  } else {
    return [];
  }
});

function applySortBy(key: string) {
  const sortByResponse = sortBy(sortKey.value, sortOrder.value, key);

  sortKey.value = sortByResponse.sortKey;
  sortOrder.value = sortByResponse.sortOrder;

  router.push({
    query: {
      ...route.query,
      resultSortKey: sortKey.value,
      resultSortOrder: sortOrder.value.toString(),
    },
    hash: route.hash,
  });
}

function intializeSorting() {
  const querySortKey = route.query.resultSortKey as string;
  const querySortOrder = parseInt(route.query.resultSortOrder as string, 10);

  if (querySortKey) {
    sortKey.value = querySortKey;
  }

  if (
    !isNaN(querySortOrder) &&
    (querySortOrder === 1 || querySortOrder === -1)
  ) {
    sortOrder.value = querySortOrder;
  }
}

onMounted(async () => {
  intializeSorting();
});
</script>

<style scoped>
* {
  font-family: var(--sc-montserrat-font-family);
}

div.table-wrapper {
  overflow: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
  overflow-x: auto;
  min-width: 600px;
}

table tr {
  height: 48px;
}

table td {
  padding: 8px 15px 9px 15px;
}

table th {
  padding: 9px 10px;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  cursor: pointer;
  text-align: left;
}

.header-label {
  display: inline-flex;
  gap: 0.5em;
  align-items: center;
  flex-wrap: wrap;
}

.header-label span[class^="icon-"]:before,
.header-label span[class*=" icon-"]:before {
  font-size: 12px;
}

table tbody tr:hover {
  box-shadow: 0px 0px 10px #00000029;
}

table tbody tr:nth-child(even) {
  background-color: #ffffff;
}

table tbody tr:nth-child(even):hover {
  background-color: #ced1d9;
}

table tbody tr:nth-child(odd):hover {
  background-color: #ced1d9;
}

div.table-wrapper.freezeHeaderRow table {
  overflow: visible;
}

div.table-wrapper.freezeHeaderRow table tbody {
  display: block;
  overflow: auto;
}

div.table-wrapper.freezeHeaderRow table thead {
  position: relative;
}

div.table-wrapper.freezeHeaderRow thead,
div.table-wrapper.freezeHeaderRow tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

@media screen and (max-width: 620px) {
  .header-label {
    flex-direction: column;
  }
}
</style>
